import React from 'react';
import {
  GetSystemResponse,
  Group,
  Part,
  DataType,
  UnitDefinition,
  OverlapGroupLinkResponse,
  UpdateOverlapGroupLinkRequest,
  Metadata,
  CopyRule,
} from '../models/Api';
import { WorkflowSteps } from '../models/WorkflowSteps';
import { PartNextAction } from '../models/PartNextAction';
import { MetadataUnion, PartUnion } from '../models/GroupUnion';
import { Button } from '@mui/material';
import { t } from 'i18next';
import SystemService from '../services/SystemService';
import styles from '../components/PartCard/PartCard.module.scss';
import DialogBox from '../components/DialogBox/DialogBox';
import SizingToolService from '../services/SizingToolService';
import LegacySizingTool from '../components/SizingTools/LegacySizingTool';
import { ITabDetail, ITabSystemData } from '../pages/DynamicTabSystem/DynamicTabSystem';
import SpinnerService from '../services/SpinnerService';
import { getIncompleteLinkedPartsToSegment, isPartMountedOnManifold, getNonLinkedPartsToSegment } from '../components/DashboardCanvas/ManifoldCanvasUtil';
import { UserCookie } from '../models/UserCookie';
import * as jose from 'jose';

const DivisionPDN = 687577;
const DivisionPDNEurope = 687378;
const DivisionSVD = 840599;

export function isManifoldOptimized(manifoldGroupId: number) {
  const system = SystemService.getCurrentState().system;
  const manifoldGroup = system?.groups?.find((manifoldGroupDetails) => manifoldGroupDetails.id === manifoldGroupId);
  const isOptimized = !!manifoldGroup?.partAssemblyData;
  return isOptimized;
}

export function confirmManifoldRevert(
  showOptimizedManifoldAlert: boolean,
  setShowOptimizedManifoldAlert: React.Dispatch<React.SetStateAction<boolean>>,
  setRevertManifold: React.Dispatch<React.SetStateAction<boolean>>,
  manifoldGroupId: number,
) {
  return (
    <DialogBox
      title={t('OptimizedManifoldAlertTitle')}
      message={t('OptimizedManifoldAlertMessage')}
      showModalState={showOptimizedManifoldAlert}
      confirmOptions={{
        confirmTranslationKey: 'Yes',
        confirmAction: async () => {
          const reverted = await SystemService.revertManifold({ manifoldGroupId });
          setRevertManifold(reverted);
        },
      }}
      closeAction={() => {
        setShowOptimizedManifoldAlert(false);
      }}
    />
  );
}
export function manifoldStationDeleteAlert(
  showDeleteManifoldValidation: boolean,
  setShowDeleteManifoldValidation: React.Dispatch<React.SetStateAction<boolean>>,
) {
  return (
    <DialogBox
      title={t('CannotDeleteGroup')}
      message={t('CannotDeleteGroupMessage')}
      showModalState={showDeleteManifoldValidation}
      closeAction={() => {
        setShowDeleteManifoldValidation(false);
      }}
    />
  );
}

export function isPDNDivision(divisionId: number | undefined) {
  return divisionId === DivisionPDN || divisionId === DivisionPDNEurope;
}

export function getDataPrecisionForDivision(divisionId: number) {
  const isEMD = divisionId !== DivisionPDN && divisionId !== DivisionPDNEurope && divisionId !== DivisionSVD;
  return isEMD ? 3 : 4;
}
export function getManifoldSegmentGroupId(part: PartUnion, system: GetSystemResponse | undefined) {
  if (isPartMountedOnManifold(part.instance)) return part.instance.groupInstanceId;
  const isMasterPart = part.category.name === 'PneumaticManifoldMasterPart';
  if (isMasterPart) {
    const segemntGroup = system?.groups?.find((group) => group.parentGroupId === part.instance.groupInstanceId);
    return segemntGroup?.id;
  }
  return undefined;
}
export function getUniqueName(system: GetSystemResponse, name: string, isGroup: boolean, isRename: boolean) {
  const baseName = name;
  const entities = isGroup ? (system?.groups as Group[]) : (system?.parts as Part[]);
  let counter = 1;
  let isDuplicateName: boolean;
  let newName: string;
  do {
    const uniqueName = isRename ? baseName : `${baseName} ${counter++}`;
    isDuplicateName = entities.some(
      (entity) =>
        entity.name?.localeCompare(uniqueName, undefined, {
          sensitivity: 'accent',
        }) === 0,
    ) as boolean;
    newName = uniqueName;
    isRename = false;
  } while (isDuplicateName);
  return newName;
}

export function formatDecimals(dataType: DataType | undefined, value: string, decimalsToDisplay: number): string {
  if (dataType === 'Double' && value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return '';
    return String(parseFloat(numericValue.toFixed(decimalsToDisplay)));
  }
  return value;
}

export function toDecimals(value: number, divisionId: number, overrideDecimalsToDisplay?: number) {
  if (isNaN(value)) return '';
  const decimalsToDisplay = overrideDecimalsToDisplay ? overrideDecimalsToDisplay : getDataPrecisionForDivision(divisionId);
  return String(parseFloat(value.toFixed(decimalsToDisplay)));
}

export function findWorkflowStep(part: Part) {
  if (part.workflowSteps?.some((step) => step.name === 'MyProjects' && step.isCompleted === true)) {
    return WorkflowSteps.MyProjects;
  } else if (part.workflowSteps?.some((step) => step.name === 'Configure' && step.isCompleted === true)) {
    return WorkflowSteps.Configure;
  } else if (part.workflowSteps?.some((step) => step.name === 'Size' && step.isCompleted === true)) {
    return WorkflowSteps.Size;
  } else {
    return WorkflowSteps.Add;
  }
}

function addTab(part: Part | undefined, sizingTool: JSX.Element | null, title?: string | null, type?: string | null) {
  const addConfigureTab = !part;
  const prev = SizingToolService.getTabSystemDataPrevState();

  const modified: ITabSystemData = {
    details: [],
    activeTabWorkflowId: prev.activeTabWorkflowId,
  };

  for (let i = 0; i < prev.details.length; i++) {
    const prevDetail = prev.details[i];
    if (type === 'CAD') {
      if (prevDetail.workflowId !== 'configure' && prevDetail.workflowId !== 'CAD') {
        const modifiedDetail = { ...prevDetail };
        modified.details.push(modifiedDetail);
      }
    } else {
      if (prevDetail.workflowId !== 'configure') {
        const modifiedDetail = { ...prevDetail };
        modified.details.push(modifiedDetail);
      }
    }
  }

  let newDetail: ITabDetail;

  if (addConfigureTab) {
    newDetail = {
      title: title as string,
      workflowId: type === 'CAD' ? 'CAD' : 'configure',
      usableComponent: sizingTool,
    };
  } else {
    newDetail = {
      title: title ? title : part?.name ?? 'part',
      workflowId: part?.workflowId ?? '',
      usableComponent: sizingTool,
    };
  }

  modified.details.push(newDetail);
  modified.activeTabWorkflowId = newDetail.workflowId;

  SizingToolService.assignTabSystemDataState(modified);
}

export function launchSizingTool(system: GetSystemResponse | undefined, part: Part) {
  const tool = SizingToolService.getSizingTool(system as GetSystemResponse, part);
  addTab(part, tool, SizingToolService.getSizingToolTitle(system as GetSystemResponse, part));
  SizingToolService.assignTabState('sizing', true);
  SizingToolService.setSizingToolState(part, false, false, true);
}

export function launchConfigureTool(part: Part | undefined, url: string, title: string, type?: string | null) {
  const tool = <LegacySizingTool iframeUrl={url} />;
  addTab(undefined, tool, title, type);
}

type LinkedPartState = { isSized: boolean; isPartAssembly: boolean; partId?: string; partAssemblyGroupId?: number };
export function getLinkedPartState(partId: string): LinkedPartState {
  const result = { isSized: false } as LinkedPartState;
  const system = SystemService?.getCurrentState()?.system as GetSystemResponse;
  const thisPartLink = system.partLinks?.find((partlink) => partlink.startId === partId);
  if (thisPartLink) {
    const linkedPartId = thisPartLink.endId;
    const linkedPart = system.parts?.find((part) => part.id === linkedPartId) as Part;
    result.isSized = !!linkedPart.workflowSteps?.find((step) => step.name === 'Configure')?.isCompleted;
    result.partId = linkedPartId;
  } else {
    const thisPart = system.parts?.find((part) => part.id === partId) as Part;
    const overlappingGroup = system.overlapGroupLinks?.find((overlapGroup) => overlapGroup.overlappingGroupId === thisPart.groupInstanceId);
    if (!overlappingGroup) {
      return result;
    }
    const linkedPartAssemblyGroupId = system.groups?.find((group) => group.id === overlappingGroup?.intersectedGroupId)?.parentGroupId as number;
    const linkedFirstPart = system.parts?.find((part) => part.groupInstanceId === linkedPartAssemblyGroupId) as Part;
    result.isSized = !!linkedFirstPart.workflowSteps?.find((step) => step.name === 'Configure')?.isCompleted;
    result.isPartAssembly = !!overlappingGroup;
    result.partAssemblyGroupId = linkedPartAssemblyGroupId;
    result.partId = overlappingGroup?.intersectedPartId;
  }
  return result;
}

export function getNextActionForPart(part: Part): PartNextAction {
  if (part.isParkerPart) {
    if (findWorkflowStep(part) < WorkflowSteps.Configure) {
      return PartNextAction.HelpMeFindTheRightPart;
    } else if (findWorkflowStep(part) === WorkflowSteps.Configure) {
      return PartNextAction.Configure;
    }
  } else {
    if (part.dataItems?.some((dataItem) => dataItem.key === 'SummaryReportData') === false) {
      return PartNextAction.EnterPartInformation;
    } else if (part.dataItems?.some((dataItem) => dataItem.key === 'SummaryReportData')) {
      return PartNextAction.ViewEditPartInformation;
    }
  }
  return PartNextAction.None;
}

export function getNextActionButtonForPart(
  part: Part,
  isSizingTabOpen: () => boolean,
  openSizingAlertDialog: () => void,
  setValidateConfigureParts: React.Dispatch<React.SetStateAction<{ incompleteParts: string; showManifoldValidationAlert: boolean }>>,
  partUnion: PartUnion,
  setShowConfirmGenericPartEditing: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const nextAction = getNextActionForPart(part);
  const actionMap = new Map();
  actionMap.set(PartNextAction.HelpMeFindTheRightPart, () => {
    if (isSizingTabOpen()) {
      openSizingAlertDialog();
    } else {
      const segmentGroupId = getManifoldSegmentGroupId(partUnion, SystemService.getCurrentState().system);
      if (segmentGroupId) {
        const nonLinkedParts = getNonLinkedPartsToSegment(segmentGroupId);
        if (nonLinkedParts.length !== 0) {
          setValidateConfigureParts({
            incompleteParts: nonLinkedParts.toString().replaceAll(',', ', '),
            showManifoldValidationAlert: true,
          });
        } else {
          const incompleteParts = getIncompleteLinkedPartsToSegment(segmentGroupId);
          if (incompleteParts.length === 0) {
            launchSizingTool(SystemService.getCurrentState().system, part);
          } else {
            setValidateConfigureParts({
              incompleteParts: incompleteParts.toString().replaceAll(',', ', '),
              showManifoldValidationAlert: true,
            });
          }
        }
      }
    }
  });
  actionMap.set(PartNextAction.Configure, () => {
    if (isSizingTabOpen()) {
      openSizingAlertDialog();
    } else {
      const sizingToolState = SizingToolService.getCurrentState();
      SizingToolService.setSizingToolState(part, sizingToolState.showMiniSizing, sizingToolState.createPartSizingData, sizingToolState.sizingUnderway);
      launchConfigureTool(part, `${process.env.REACT_APP_WORKFLOW_ROUTER_URL}${part.workflowId}`, 'configure');
    }
  });
  actionMap.set(PartNextAction.ViewEditPartInformation, () => {
    const linkedPartState = getLinkedPartState(part.id as string);
    if (linkedPartState.isSized) {
      setShowConfirmGenericPartEditing(true);
      return;
    }
    SizingToolService.setSizingToolState(part, true, false, false);
    SpinnerService.updateSpinnerStatus(0, false);
  });
  actionMap.set(PartNextAction.EnterPartInformation, () => {
    SizingToolService.setSizingToolState(part, true, false, false);
    SpinnerService.updateSpinnerStatus(0, false);
  });
  if (nextAction == PartNextAction.None) {
    return <Button className={styles['button-placeholder']}></Button>;
  }
  return (
    <Button variant="outlined" className={styles['part-card-button']} onClick={actionMap.get(nextAction)}>
      {t(nextAction.toString())}
    </Button>
  );
}

type DeleteGroupDialog = { show: boolean; withApplicationGroups: boolean };
export function deleteGroupConfirmDialog(
  group: Group,
  showDeleteGroupDialog: boolean,
  setDeleteGroupDialog: React.Dispatch<React.SetStateAction<DeleteGroupDialog>>,
  withApplicationGroups: boolean,
  setShowOptimizedManifoldAlert: React.Dispatch<React.SetStateAction<boolean>>,
  revertManifoldOptimize: boolean,
  setManifoldActioName: React.Dispatch<React.SetStateAction<string>>,
  setShowDeleteManifoldValidation: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const deleteGroupHandler = () => {
    // This Delete Group confirmation dialog is reused for the following use cases:
    // 1. Delete a (regular) group and all its content (subgroups and parts)
    // 2. Delete a virtual group: deletes a (regular) group and all its content (subgroups and parts), the link between this group
    //    and its corresponding intersected part, and the intersected part (which is the key/main thing to delete in this case)
    // 3. Delete a group and all its content as well as all groups that overlap with this group or any of its child groups
    setManifoldActioName('delete');
    const system = SystemService.getCurrentState().system;
    const virtualGroup = system?.virtualGroups?.find((virtualGroupObj) => virtualGroupObj.baseGroupId === group.id);
    if (withApplicationGroups) {
      // In the future, we might have to rename this to something more generic since Manifold is very PDN-specific
      SystemService.deleteManifoldWithVirtualGroups(group.id as number);
    } else if (virtualGroup) {
      const valveId = virtualGroup?.intersectedPartId as string;
      const manifoldGroupId = system?.groups?.find((groupObj) => groupObj.id === virtualGroup.intersectedGroupId)?.parentGroupId as number;
      SystemService.deleteValveFromManifold(manifoldGroupId, valveId, group.id as number, setShowOptimizedManifoldAlert, setShowDeleteManifoldValidation);
    } else {
      SystemService.deleteGroup(group.id as number);
    }
  };
  if (revertManifoldOptimize) {
    deleteGroupHandler();
    return;
  }
  return (
    <DialogBox
      title={t('ConfirmDelete')}
      message={!withApplicationGroups ? t('DeleteGroupMessage', { group }) : t('DeleteGroupAndApplicationGroupsMessage', { group })}
      confirmOptions={{
        confirmTranslationKey: 'Delete',
        confirmAction: deleteGroupHandler,
      }}
      showModalState={showDeleteGroupDialog}
      closeAction={() => {
        setDeleteGroupDialog({ show: false, withApplicationGroups: false });
      }}
    />
  );
}

export function deletePartConfirmDialog(part: Part, showDeletePartDialog: boolean, setShowDeletePartDialog: React.Dispatch<React.SetStateAction<boolean>>) {
  return (
    <DialogBox
      title={t('ConfirmDelete')}
      message={t('DeletePartMessage', { part })}
      confirmOptions={{
        confirmTranslationKey: 'Delete',
        confirmAction: () => SystemService.deletePart(part.id as string),
      }}
      showModalState={showDeletePartDialog}
      closeAction={() => {
        setShowDeletePartDialog(false);
      }}
    />
  );
}
export async function createGroup(systemId: string, definitionId: number, name: string, isFinished: boolean | undefined, parentGroupId: null) {
  const createGroupRequest = {
    systemId: systemId,
    definitionId: definitionId,
    name: name,
    isFinished: isFinished,
    parentGroupId: parentGroupId,
  };

  const createGroupResponse = await SystemService.createGroup(createGroupRequest);

  return createGroupResponse;
}

export async function createPart(
  groupInstanceId: number,
  groupIsFinished: boolean,
  categoryId: number,
  name: string,
  row: number,
  column: number,
  order: number,
) {
  const createPartRequest = {
    groupInstanceId: groupInstanceId,
    groupIsFinished: groupIsFinished,
    categoryId: categoryId,
    name: name,
    row: row,
    column: column,
    order: order,
  };
  const createdPart = await SystemService.createPart(createPartRequest);
  return createdPart;
}

export const getManifoldGroups = (system: GetSystemResponse) => {
  const groups = system.groups || [];
  return groups.filter(
    (group) => system?.groupDefinitions?.find((definition) => definition.id === group.definitionId)?.uniqueName === 'PneumaticSupplyManifolds',
  );
};

export const needsConversion = (metadata: MetadataUnion) => {
  const shouldConvert =
    (metadata.definition.dataType == DataType.Int || metadata.definition.dataType == DataType.Double) &&
    metadata.unitDefinition?.unitGroup !== undefined &&
    metadata.unitDefinition?.unitSubgroup !== undefined &&
    metadata.unitDefinition?.unit !== undefined;
  return shouldConvert;
};

export const getDefaultUnitForUnitSubgroup = (units: UnitDefinition[], part: MetadataUnion, isMetricUnit: boolean) => {
  const partDefinition = part.definition;
  const currentUnit = units?.find(
    (unit: UnitDefinition) =>
      unit?.unitGroup == partDefinition.unitGroup &&
      unit?.unitSubgroup == partDefinition.unitSubGroup &&
      (isMetricUnit ? unit?.isDefaultForMetric : unit?.isDefaultForImperial),
  );
  return currentUnit ? currentUnit : part.unitDefinition;
};

export const convertUnitTo = (unitGroup: string, unitBefore: number, valueBefore: number, unitCurrent: number) => {
  const unitValue = window[`unitSelector${unitGroup}UnitsConvert`](unitBefore, valueBefore, unitCurrent);
  return unitValue;
};

export const isPDNMultiPartSystem = () => {
  return SystemService.getCurrentState().system?.templateTypeName === 'Pneumatic Multi-Part System';
};

export const convertUnit = (unitDefinitions: UnitDefinition[], partMetadata: MetadataUnion, isMetric: boolean) => {
  const value = convertUnitTo(
    partMetadata.unitDefinition.unitGroup as string,
    partMetadata.unitDefinition.unit as number,
    parseFloat(partMetadata.instance.value),
    getDefaultUnitForUnitSubgroup(unitDefinitions, partMetadata, isMetric).unit as number,
  );
  return value;
};

export const getConvertedLabel = (unitDefinitions: UnitDefinition[], metadataUnion: MetadataUnion, isMetric: boolean) => {
  return getDefaultUnitForUnitSubgroup(unitDefinitions, metadataUnion, isMetric)?.unitLabel;
};

export const sortByOrder = (parts: Part[]) => {
  return parts.sort((a, b) => (a.order as number) - (b.order as number));
};

export const createValveRequest = (
  i: number,
  startingRow: number,
  systemCopy: GetSystemResponse,
  partBaseName: string,
  partCategoryId: number,
  valveNames: { name: string }[],
) => {
  // Eventually, a second manifold would start on a much higher row (e.g. 18)
  if (valveNames.length > 0) {
    systemCopy.parts = [...(systemCopy.parts as Part[]), ...valveNames];
  }
  const uniqueName = getUniqueName(systemCopy, partBaseName as string, false, false);
  valveNames.push({ name: uniqueName });
  return {
    groupInstanceId: 0,
    groupIsFinished: true,
    categoryId: partCategoryId,
    name: uniqueName,
    row: startingRow + i,
    column: 2, // Safe for the time being to assume valves go on the second column
    order: 1 + i,
  };
};

export const updateOverlapGroupLinkWithLinkedPart = (part: Part, overlapGroupLink: OverlapGroupLinkResponse) => {
  if (!part && !overlapGroupLink) {
    return;
  }

  const overlapLinkRequest = {} as UpdateOverlapGroupLinkRequest;
  overlapLinkRequest.id = overlapGroupLink.id;
  overlapLinkRequest.linkState = overlapGroupLink.linkState;
  overlapLinkRequest.linkedPartId = part.id;
  SystemService.updateOverlapGroupLink(overlapLinkRequest);
  return;
};

export const isGroupLastInLine = (system: GetSystemResponse, groupId: number) => {
  return !system.groupLinks?.some((groupLink) => groupLink.startId === groupId);
};

export const isPartDeletable = (system: GetSystemResponse, part: Part) => {
  const isFirstPartOfVirtualGroup = system?.overlapGroupLinks?.some((overlapGroup) => overlapGroup.linkedPartId === part.id);
  const isPartGroupLastInLine = part.groupInstanceId && isGroupLastInLine(system, part.groupInstanceId);
  const isLinked = system.partLinks?.some((partlink) => partlink.startId === part.id);
  const isOnlyPartInGroup = system.parts?.filter((_part) => _part.groupInstanceId === part.groupInstanceId).length === 1;
  return (isOnlyPartInGroup || (!isLinked && !isFirstPartOfVirtualGroup)) && isPartGroupLastInLine;
};

export const convertDecimalStringToNumber = (value: string, locale: string | null | undefined) => {
  // Extract the thousands separator and decimal separator from the formatted number string
  const [, decimalSeparator] = Array.from((1.42).toLocaleString(locale as string));
  const thousandsSeparator = decimalSeparator === '.' ? ',' : '.';
  // Remove thousand separators and put a point where the decimal separator occurs
  const formattedString = Array.from(value, (c) => (c === thousandsSeparator ? '' : c === decimalSeparator ? '.' : c)).join('');

  // Now it can be parsed
  const finalValue = parseFloat(formattedString);
  return finalValue;
};

export const getNumberValidator = (value: string, locale: string) => {
  const [, decimalSeparator] = Array.from((1.42).toLocaleString(locale));
  const thousandsSeparator = decimalSeparator === '.' ? ',' : '.';
  const localeAwareRegex = new RegExp(`^[-]?([0-9]{1,3}(${thousandsSeparator}?[0-9]{3})*(\\${decimalSeparator}[0-9]+)?|d*\\${decimalSeparator}d+|d+)$`, 'i');
  const isValidNumber = localeAwareRegex.test(value);
  if (!isValidNumber) {
    return NaN;
  }
  return convertDecimalStringToNumber(value, locale);
};

export const copyMetadataToLinkedPart = (sourcePartId: string) => {
  const system = SystemService?.getCurrentState()?.system as GetSystemResponse;
  const source = system.parts?.find((part) => part.id === sourcePartId) as Part;
  const partLink = system?.partLinks?.find((_partLink) => _partLink.startId === sourcePartId);
  const overlapGroupLink = system?.overlapGroupLinks?.find((_overlapGroupLink) => _overlapGroupLink.linkedPartId === sourcePartId);
  if (!partLink && !overlapGroupLink) {
    return undefined;
  }

  const targetId = partLink ? partLink.endId : overlapGroupLink?.intersectedPartId;
  const target = system.parts?.find((part) => part.id === targetId) as Part;
  const sourceDataInstances = source.metadataInstances as Metadata[];
  const targetMetadata = [...(target?.metadataInstances as Metadata[])];

  for (const sourceDataInstance of sourceDataInstances) {
    const copyRule = system?.copyRules?.find((rule) => rule.sourceDefinitionId === sourceDataInstance.definitionId) as CopyRule;

    if (!copyRule) {
      continue;
    }

    let targetDataInstance = target?.metadataInstances?.find((dataInstance) => dataInstance.definitionId === copyRule?.destinationDefinitionId);

    if (!targetDataInstance) {
      targetDataInstance = {
        id: 0,
        definitionId: copyRule?.destinationDefinitionId,
      };
      targetMetadata.push(targetDataInstance);
    }

    targetDataInstance.value = sourceDataInstance.value;
    targetDataInstance.isEditable = sourceDataInstance.isEditable;
    targetDataInstance.selectedUnit = sourceDataInstance.selectedUnit;
    targetDataInstance.origin = sourceDataInstance.origin;
    targetDataInstance.originalSetter = sourceDataInstance.originalSetter;
  }
  return { targetPartId: target?.id, metadataInstances: targetMetadata };
};

export function getBasePath(url = process.env.PUBLIC_URL) {
  if (url.endsWith('vnext')) {
    return '/vnext';
  }
  if (url.endsWith('rnext')) {
    return '/rnext';
  }
  return '/';
}

export const getLegacyHostname = () => new URL(process.env.REACT_APP_LEGACY_BASE_HOST || document.URL).hostname.split('.')[0];

const userCookieName = 'PARKER_VE_USER';
const hostSpecificUserCookieSuffix = 'User';
export const getUserFromCookie = () => {
  let veUser =
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${getLegacyHostname()}${hostSpecificUserCookieSuffix}=`))
      ?.split('=')[1] || undefined;
  if (!veUser) {
    veUser =
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(userCookieName))
        ?.split('=')[1] || undefined;
  }
  if (veUser) {
    const userSettings = JSON.parse(decodeURIComponent(veUser)) as UserCookie;
    let parkerLocale = userSettings.locale;
    const queryString = new URLSearchParams(document.location.search);
    const languageCode = queryString.get('languageCode')?.toLowerCase();
    const countryCode = queryString.get('countryCode')?.toUpperCase() || 'US';
    parkerLocale = languageCode && countryCode ? `${languageCode}-${countryCode}` : parkerLocale;
    parkerLocale = parkerLocale || navigator.language;
    userSettings.locale = parkerLocale;
    return userSettings;
  }
  return undefined;
};

export const getAccessToken = () => {
  const legacyHostname = getLegacyHostname();
  const accessToken =
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${legacyHostname}AccTok=`))
      ?.split('=')[1] || undefined;
  return accessToken;
};

export const getTokenClaims = (accessToken?: string) => {
  const token = accessToken ?? getAccessToken();
  const claims = token ? jose.decodeJwt(token) : {};
  return claims;
};

export function getUserFullName() {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('UserFullName'))
      ?.split('=')[1] ?? ''
  );
}

export const isMetricUnitsInUse = () => {
  const metricUnits = 3;
  const system = SystemService?.getCurrentState()?.system;
  if (system) {
    return !!system.displayInMetricUnits;
  }
  const user = getUserFromCookie();
  const useMetricUnits = user?.useMetricUnits || metricUnits;
  return useMetricUnits === metricUnits;
};

export async function genericPartEditingConfirmed(part: Part) {
  const system = SystemService?.getCurrentState()?.system as GetSystemResponse;
  const linkedPartState = getLinkedPartState(part.id as string);
  if (linkedPartState.isPartAssembly) {
    const partAssemblyGroupId = linkedPartState.partAssemblyGroupId as number;
    const partAssemblyGroup = system.groups?.find((group) => group.id === partAssemblyGroupId);
    const partAssemblyGroupDefinition = system.groupDefinitions?.find((groupDefinition) => groupDefinition.id === partAssemblyGroup?.definitionId);
    const intersectedPart = system.parts?.find((_part) => _part.id === linkedPartState.partId) as Part;
    await SizingToolService.resetWorkFlow(intersectedPart.workflowId as string);
    await SystemService.refreshSystemById(system.id as string);
    if (partAssemblyGroupDefinition?.uniqueName === 'PneumaticSupplyManifolds' && isManifoldOptimized(partAssemblyGroupId)) {
      const reverted = await SystemService.revertManifold({ manifoldGroupId: partAssemblyGroupId });
      if (!reverted) {
        return;
      }
    }
  } else {
    const linkedPart = system.parts?.find((_part) => _part.id === linkedPartState.partId) as Part;
    await SizingToolService.resetWorkFlow(linkedPart.workflowId as string);
    await SystemService.createPartSizingData(linkedPart);
    await SystemService.refreshPartById(system?.id as string, linkedPart.id as string);
  }
  // Open "mini-sizing" dialog
  SizingToolService.setSizingToolState(part, true, false, false);
  SpinnerService.updateSpinnerStatus(0, false);
}
